/**
 * 登录后的落地路由。
 *
 * 顺序：embed 仍进决策支持页；显式 redirect 优先；否则上次可用工作站；
 * 再按角色默认站；最后优先取门诊信息系统下的可用站（没有则取注册表第一个可用站）。
 * 没有任何可用站时返回 null，由调用方决定是否仍打开 /medical，避免权益加载失败把人锁在外面。
 */

import {
  getWorkstation,
  isWorkstationAvailable,
  listAvailableWorkstations,
  type WorkstationCode,
  type WorkstationGates,
} from "@/config/workstations";

export const LAST_WORKSTATION_KEY = "pgy.lastWorkstation";

export function rememberWorkstation(code: string): void {
  try {
    sessionStorage.setItem(LAST_WORKSTATION_KEY, code);
  } catch {
    /* 隐私模式写失败不影响进入页面 */
  }
}

export function readLastWorkstation(): string | null {
  try {
    return sessionStorage.getItem(LAST_WORKSTATION_KEY);
  } catch {
    return null;
  }
}

function safeRedirect(redirect: string | null | undefined): string | null {
  if (!redirect) return null;
  if (!redirect.startsWith("/") || redirect.startsWith("//")) return null;
  const pathOnly = redirect.split("?")[0];
  if (pathOnly === "/login" || pathOnly.startsWith("/login/")) return null;
  if (pathOnly === "/") return null;
  return redirect;
}

function roleDefaultCode(
  role: string | null | undefined,
): WorkstationCode | null {
  if (role === "cashier") return "registration_desk";
  if (role === "pharmacist") return "outpatient_pharmacy";
  if (role === "inpatient_pharmacist") return "inpatient_pharmacy";
  if (role === "doctor") return "outpatient_doctor";
  if (role === "nurse") return "nurse";
  return null;
}

export function resolveHomeRoute(input: {
  embed: boolean;
  redirect?: string | null;
  role?: string | null;
  has: (moduleCode: string) => boolean;
  codes?: readonly string[];
  lastWorkstation?: string | null;
}): string | null {
  if (input.embed) return "/medical?embed=1";
  const redirect = safeRedirect(input.redirect);
  if (redirect) return redirect;
  const gates: WorkstationGates = {
    has: input.has,
    role: input.role ?? null,
    codes: input.codes ?? [],
  };
  const lastCode = input.lastWorkstation;
  if (lastCode) {
    const last = getWorkstation(lastCode as WorkstationCode);
    if (last && isWorkstationAvailable(last, gates)) return last.defaultRoute;
  }
  const preferred = roleDefaultCode(input.role);
  if (preferred) {
    const station = getWorkstation(preferred);
    if (station && isWorkstationAvailable(station, gates)) {
      return station.defaultRoute;
    }
  }
  const fallback = listAvailableWorkstations(gates);
  // 无角色默认站时优先落门诊信息系统：注册表首项是决策支持站
  // （code=outpatient / system=cdss / route=/medical），直接取首项会把管理员等
  // 未列角色送进决策支持页；买了门诊系统的客户应直接进入门诊信息系统。
  const outpatientFirst = fallback.find(
    (station) => station.system === "outpatient",
  );
  return (outpatientFirst ?? fallback[0])?.defaultRoute ?? null;
}
